import instance from "./interceptor";

export type ClientErrorSource =
  "error-boundary" | "window-error" | "unhandled-rejection";

export type ClientErrorReport = {
  message: string;
  stack?: string;
  componentStack?: string;
  source: ClientErrorSource;
  route?: string;
  userAgent?: string;
  requestId?: string;
};

const DEDUPE_WINDOW_MS = 10_000;
const MAX_FIELD_LENGTH = 5000;

const recentReports = new Map<string, number>();
let globalHandlersInstalled = false;

const toError = (value: unknown): Error => {
  if (value instanceof Error) return value;
  if (typeof value === "string") return new Error(value);
  return new Error("Unknown client error");
};

const truncate = (value: string | undefined, maxLength = MAX_FIELD_LENGTH) => {
  if (!value) return undefined;
  return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
};

const buildDedupeKey = (report: ClientErrorReport) =>
  [
    report.source,
    report.message,
    report.stack?.slice(0, 300),
    report.componentStack?.slice(0, 300),
  ].join("|");

const shouldReport = (report: ClientErrorReport) => {
  const now = Date.now();
  const key = buildDedupeKey(report);
  const lastReportedAt = recentReports.get(key);

  if (lastReportedAt && now - lastReportedAt < DEDUPE_WINDOW_MS) {
    return false;
  }

  recentReports.set(key, now);
  for (const [recentKey, timestamp] of recentReports) {
    if (now - timestamp > DEDUPE_WINDOW_MS) {
      recentReports.delete(recentKey);
    }
  }

  return true;
};

export const reportClientError = async (report: ClientErrorReport) => {
  const sanitizedReport: ClientErrorReport = {
    message: truncate(report.message, 2000) || "Unknown client error",
    stack: truncate(report.stack),
    componentStack: truncate(report.componentStack),
    source: report.source,
    route: truncate(report.route || globalThis.location?.pathname, 300),
    userAgent: truncate(
      report.userAgent || globalThis.navigator?.userAgent,
      500,
    ),
    requestId: truncate(report.requestId, 100),
  };

  if (!shouldReport(sanitizedReport)) return;

  try {
    await instance.post("/client-errors", sanitizedReport);
  } catch {
    // Reporting must never cause an error loop or block the user.
  }
};

export const installGlobalErrorHandlers = () => {
  if (globalHandlersInstalled || typeof window === "undefined") return;
  globalHandlersInstalled = true;

  window.addEventListener("error", (event) => {
    const error = toError(event.error || event.message);
    void reportClientError({
      message: error.message,
      stack: error.stack,
      source: "window-error",
      route: window.location.pathname,
      userAgent: window.navigator.userAgent,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const error = toError(event.reason);
    void reportClientError({
      message: error.message,
      stack: error.stack,
      source: "unhandled-rejection",
      route: window.location.pathname,
      userAgent: window.navigator.userAgent,
    });
  });
};

export const __resetClientErrorReporterForTests = () => {
  recentReports.clear();
  globalHandlersInstalled = false;
};
